import { z } from 'zod'

// Mensagem padrão para campos obrigatórios
const REQUIRED_MESSAGE = 'Este campo é obrigatório.'

/**
 * Validação para string obrigatória
 */
export const zRequiredString = z.string().min(1, REQUIRED_MESSAGE)

/**
 * Validação para data obrigatória (string no formato YYYY-MM-DD)
 * O input type="date" já garante o formato correto, então validamos apenas se é uma data válida
 */
export const zRequiredDate = z.string()
  .min(1, REQUIRED_MESSAGE)
  .refine((val) => {
    if (!val || val.trim() === '') return false
    // Validar formato YYYY-MM-DD (input type="date" já garante isso, mas validamos por segurança)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(val)) return false
    // Validar se é uma data válida
    const date = new Date(val + 'T00:00:00') // Adicionar hora para evitar problemas de timezone
    if (isNaN(date.getTime())) return false
    // Validar se a data corresponde ao valor inserido (evita datas como 2025-13-45)
    const [year, month, day] = val.split('-').map(Number)
    return date.getFullYear() === year && 
           date.getMonth() + 1 === month && 
           date.getDate() === day &&
           year >= 1900 && year <= 2100 // Limites razoáveis
  }, 'Data inválida. Selecione uma data válida.')

/**
 * Validação para data opcional (string no formato YYYY-MM-DD)
 * O input type="date" já garante o formato correto, então validamos apenas se é uma data válida
 */
export const zOptionalDate = z.union([
  z.string().refine((val) => {
    if (!val || val.trim() === '') return true // Vazio é válido para opcional
    // Validar formato YYYY-MM-DD (input type="date" já garante isso, mas validamos por segurança)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(val)) return false
    // Validar se é uma data válida
    const date = new Date(val + 'T00:00:00') // Adicionar hora para evitar problemas de timezone
    if (isNaN(date.getTime())) return false
    // Validar se a data corresponde ao valor inserido
    const [year, month, day] = val.split('-').map(Number)
    return date.getFullYear() === year && 
           date.getMonth() + 1 === month && 
           date.getDate() === day &&
           year >= 1900 && year <= 2100 // Limites razoáveis
  }, {
    message: 'Data inválida. Selecione uma data válida.'
  }),
  z.undefined()
])

/**
 * Validação para número obrigatório
 */
export const zRequiredNumber = z.coerce.number({ message: 'Deve ser um número válido.' }).refine(val => val > 0, REQUIRED_MESSAGE)

/**
 * Validação para email obrigatório
 */
export const zRequiredEmail = z.string().email('Deve ser um email válido.').min(1, REQUIRED_MESSAGE)

/**
 * Validação para código obrigatório (converte number para string)
 * Usado para campos como statusCodigo, prioridadeCodigo, etc.
 */
export const zRequiredCode = z.preprocess(
  (val) => {
    if (val === undefined || val === null || val === '') return ''
    if (typeof val === 'number') return String(val)
    if (typeof val === 'string') return val
    return String(val)
  },
  z.string().min(1, REQUIRED_MESSAGE)
)

/**
 * Validação para ID obrigatório (número maior que 0)
 * Usado para campos como analistaSMNId, analistaDDPId, etc.
 */
export const zRequiredId = z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return 0
    const num = Number(val)
    return isNaN(num) ? 0 : num
  },
  z.number({ message: 'Deve ser um número válido.' }).min(1, REQUIRED_MESSAGE)
)

